/**
 * UYGULAMA GÖRÜNÜMÜ — ana sayfadaki "ürün resmi"
 *
 * 🔴 NEDEN ÇİZİM, NEDEN EKRAN GÖRÜNTÜSÜ DEĞİL:
 * Projede tek bir uygulama ekran görüntüsü yok (public/img'de yalnızca ödeme logoları var).
 * Gerçek panelden çekim yapmak da gerçek müşteri verisini yayınlamak demek olurdu.
 * Bu yüzden arayüz burada SVG + CSS ile çiziliyor: keskin, her ekranda net, karanlık/aydınlık
 * farkı yok, dosya boyutu sıfır ve içindeki bütün veriler TEMSİLÎDİR.
 *
 * Dört sekme, uygulamanın dört gerçek ekranını temsil eder: Servis · Cari · Stok · Rapor.
 */
import React, { useState } from 'react';
import {
  LayoutDashboard, Wrench, Users, Package, BarChart3, Search, Bell,
} from 'lucide-react';
import { cn } from '../../lib/utils';

type Sekme = 'servis' | 'cari' | 'stok' | 'rapor';

const SEKMELER: { id: Sekme; ad: string }[] = [
  { id: 'servis', ad: 'Servis Takibi' },
  { id: 'cari',   ad: 'Cari Hesap' },
  { id: 'stok',   ad: 'Stok' },
  { id: 'rapor',  ad: 'Raporlar' },
];

/* ————————————————————————— küçük parçalar ————————————————————————— */

const Rozet: React.FC<{ tur: 'olumlu' | 'uyari' | 'notr'; children: React.ReactNode }> = ({ tur, children }) => (
  <span
    className={cn(
      'inline-block whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-semibold ring-1',
      tur === 'olumlu' && 'bg-emerald-50 text-emerald-700 ring-emerald-200',
      tur === 'uyari'  && 'bg-amber-50 text-amber-700 ring-amber-200',
      tur === 'notr'   && 'bg-slate-100 text-slate-600 ring-slate-200',
    )}
  >
    {children}
  </span>
);

const OzetKart: React.FC<{ etiket: string; deger: string; alt?: string }> = ({ etiket, deger, alt }) => (
  <div className="bg-zemin-800 px-4 py-3">
    <div className="text-[10px] uppercase tracking-wider text-slate-500">{etiket}</div>
    <div className="mt-1 text-xl font-bold tabular-nums text-slate-900">{deger}</div>
    {alt && <div className="mt-0.5 text-[10px] text-slate-500">{alt}</div>}
  </div>
);

/** Aylık ciro çubukları — SVG, gerçek grafik kütüphanesi yüklemeden. */
const Grafik: React.FC = () => {
  const veri = [38, 52, 44, 61, 55, 73, 68, 84, 79, 92, 88, 100];
  const aylar = ['O', 'Ş', 'M', 'N', 'M', 'H', 'T', 'A', 'E', 'E', 'K', 'A'];
  return (
    <svg viewBox="0 0 320 110" className="h-full w-full" role="img" aria-label="Aylık ciro grafiği (temsilî)">
      {[0, 25, 50, 75].map((y) => (
        <line key={y} x1="0" x2="320" y1={90 - y * 0.8} y2={90 - y * 0.8} stroke="#E2E8F0" strokeWidth="1" />
      ))}
      {veri.map((d, i) => {
        const h = d * 0.72;
        return (
          <rect
            key={i}
            x={8 + i * 26}
            y={90 - h}
            width="14"
            height={h}
            rx="3"
            fill="#2563EB"
            opacity={i === veri.length - 1 ? 1 : 0.32 + i * 0.045}
          />
        );
      })}
      {aylar.map((a, i) => (
        <text key={i} x={15 + i * 26} y="104" textAnchor="middle" fontSize="8" fill="#94A3B8">{a}</text>
      ))}
    </svg>
  );
};

/* ————————————————————————— sekme içerikleri ————————————————————————— */

const ServisEkrani: React.FC = () => (
  <>
    <div className="grid grid-cols-2 gap-px bg-zemin-600 lg:grid-cols-4">
      <OzetKart etiket="Açık Servis" deger="28" alt="6'sı bugün geldi" />
      <OzetKart etiket="Teslime Hazır" deger="11" />
      <OzetKart etiket="Parça Bekleyen" deger="4" />
      <OzetKart etiket="Bu Ay Teslim" deger="156" />
    </div>
    <table className="w-full border-collapse text-left">
      <thead>
        <tr className="border-b border-zemin-600 bg-slate-50">
          {['Servis No', 'Müşteri', 'Cihaz', 'Durum'].map((b) => (
            <th key={b} className={cn('px-4 py-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500', b === 'Cihaz' && 'hidden sm:table-cell')}>{b}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {[
          ['SRV-0231', 'Mehmet Yılmaz',  'iPhone 13 Pro · Ekran',   'Tamamlandı',    'olumlu'],
          ['SRV-0230', 'Ayşe Demir',     'Galaxy S21 · Şarj soketi', 'İşlemde',       'uyari'],
          ['SRV-0229', 'Kalkım Market',  'Kamera kaydedici · Kurulum', 'Parça bekliyor', 'uyari'],
          ['SRV-0228', 'Hasan Aydın',    'MacBook Air · Klavye',    'Teslim edildi', 'olumlu'],
        ].map(([no, mus, cihaz, durum, tur]) => (
          <tr key={no as string} className="border-b border-slate-200 last:border-0">
            <td className="px-4 py-2.5 font-mono text-[11px] text-slate-600">{no}</td>
            <td className="px-4 py-2.5 text-xs text-slate-700">{mus}</td>
            <td className="hidden px-4 py-2.5 text-xs text-slate-600 sm:table-cell">{cihaz}</td>
            <td className="px-4 py-2.5"><Rozet tur={tur as any}>{durum}</Rozet></td>
          </tr>
        ))}
      </tbody>
    </table>
  </>
);

const CariEkrani: React.FC = () => (
  <>
    <div className="grid grid-cols-3 gap-px bg-zemin-600">
      <OzetKart etiket="Toplam Alacak" deger="₺394.269" alt="137 cari" />
      <OzetKart etiket="Vadesi Geçmiş" deger="₺62.400" />
      <OzetKart etiket="Bu Ay Tahsilat" deger="₺48.250" />
    </div>
    <table className="w-full border-collapse text-left">
      <thead>
        <tr className="border-b border-zemin-600 bg-slate-50">
          {['Cari', 'Son İşlem', 'Bakiye', 'Durum'].map((b) => (
            <th key={b} className={cn('px-4 py-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500', b === 'Son İşlem' && 'hidden sm:table-cell')}>{b}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {[
          ['Kalkım Ticaret',  'Satış faturası', '₺12.480', 'Vadesinde',  'olumlu'],
          ['Yenice Elektrik', 'Tahsilat',       '₺0',      'Kapandı',    'olumlu'],
          ['Ahmet Usta',      'Borç dekontu',   '₺3.150',  '18 gün gecikme', 'uyari'],
          ['Demir Otomotiv',  'Alış faturası',  '−₺2.400', 'Alacaklı',   'notr'],
        ].map(([ad, islem, bakiye, durum, tur]) => (
          <tr key={ad as string} className="border-b border-slate-200 last:border-0">
            <td className="px-4 py-2.5 text-xs text-slate-700">{ad}</td>
            <td className="hidden px-4 py-2.5 text-xs text-slate-600 sm:table-cell">{islem}</td>
            <td className="px-4 py-2.5 text-right font-mono text-xs tabular-nums text-slate-700">{bakiye}</td>
            <td className="px-4 py-2.5"><Rozet tur={tur as any}>{durum}</Rozet></td>
          </tr>
        ))}
      </tbody>
    </table>
  </>
);

const StokEkrani: React.FC = () => (
  <>
    <div className="grid grid-cols-3 gap-px bg-zemin-600">
      <OzetKart etiket="Ürün Çeşidi" deger="136" />
      <OzetKart etiket="Kritik Seviye" deger="24" alt="sipariş gerekiyor" />
      <OzetKart etiket="Stok Değeri" deger="₺186.900" />
    </div>
    <div className="divide-y divide-slate-200">
      {[
        ['Ekran — iPhone 13 Pro', 12, 40, 'olumlu'],
        ['Batarya — Galaxy S21',   3, 12, 'uyari'],
        ['CAT6 Kablo (metre)',   394, 100, 'olumlu'],
        ['Kamera — 4MP Dome',      2, 10, 'uyari'],
      ].map(([ad, adet, yuzde, tur]) => (
        <div key={ad as string} className="flex items-center gap-3 px-4 py-2.5">
          <div className="min-w-0 flex-1">
            <div className="truncate text-xs text-slate-700">{ad}</div>
            <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-zemin-600">
              <div
                className={cn('h-full rounded-full', tur === 'olumlu' ? 'bg-emerald-500' : 'bg-amber-500')}
                style={{ width: `${yuzde}%` }}
              />
            </div>
          </div>
          <span className="shrink-0 font-mono text-xs tabular-nums text-slate-600">{adet as number} ad.</span>
        </div>
      ))}
    </div>
  </>
);

const RaporEkrani: React.FC = () => (
  <>
    <div className="grid grid-cols-3 gap-px bg-zemin-600">
      <OzetKart etiket="Aylık Ciro" deger="₺214.800" alt="geçen aya göre +%12" />
      <OzetKart etiket="Kâr Marjı" deger="%38" />
      <OzetKart etiket="Ort. Onarım" deger="2,4 gün" />
    </div>
    <div className="px-4 pb-3 pt-4">
      <div className="mb-2 text-[10px] uppercase tracking-wider text-slate-500">Aylık ciro (12 ay)</div>
      <div className="h-[110px]"><Grafik /></div>
    </div>
  </>
);

/* ————————————————————————— ana bileşen ————————————————————————— */

const UygulamaGorunumu: React.FC = () => {
  const [sekme, setSekme] = useState<Sekme>('servis');

  const menu = [
    { ikon: LayoutDashboard, ad: 'Panel',   aktif: false },
    { ikon: Wrench,          ad: 'Servis',  aktif: sekme === 'servis' },
    { ikon: Users,           ad: 'Cari',    aktif: sekme === 'cari' },
    { ikon: Package,         ad: 'Stok',    aktif: sekme === 'stok' },
    { ikon: BarChart3,       ad: 'Rapor',   aktif: sekme === 'rapor' },
  ];

  return (
    <div>
      {/* sekmeler */}
      <div className="mb-4 flex gap-1.5 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {SEKMELER.map((s) => (
          <button
            key={s.id}
            onClick={() => setSekme(s.id)}
            aria-pressed={sekme === s.id}
            className={cn(
              'shrink-0 rounded-lg px-3.5 py-2 text-[13px] font-medium transition-colors',
              sekme === s.id
                ? 'bg-vurgu-500 text-white shadow-lg shadow-vurgu-600/25'
                : 'border border-zemin-600 bg-white text-slate-600 hover:border-slate-300 hover:text-slate-900',
            )}
          >
            {s.ad}
          </button>
        ))}
      </div>

      {/* uygulama penceresi */}
      <div className="overflow-hidden rounded-2xl border border-zemin-600 bg-zemin-800 shadow-2xl shadow-slate-300/50">
        {/* pencere başlığı */}
        <div className="flex items-center gap-2 border-b border-zemin-600 bg-slate-50 px-4 py-2.5">
          <span className="h-2.5 w-2.5 rounded-full bg-red-400" />
          <span className="h-2.5 w-2.5 rounded-full bg-amber-400" />
          <span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
          <span className="mx-auto rounded-md bg-white px-3 py-1 font-mono text-[10px] text-slate-500 ring-1 ring-slate-200">
            tservis.tr/dashboard
          </span>
        </div>

        <div className="flex">
          {/* sol menü */}
          <aside className="hidden w-[164px] shrink-0 border-r border-zemin-600 bg-slate-50 py-3 sm:block">
            {menu.map(({ ikon: I, ad, aktif }) => (
              <div
                key={ad}
                className={cn(
                  'mx-2 mb-0.5 flex items-center gap-2.5 rounded-lg px-3 py-2 text-xs',
                  aktif ? 'bg-vurgu-500/10 font-semibold text-vurgu-600' : 'text-slate-500',
                )}
              >
                <I className="h-3.5 w-3.5" />
                {ad}
              </div>
            ))}
          </aside>

          {/* içerik */}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-3 border-b border-zemin-600 px-4 py-2.5">
              <div className="flex flex-1 items-center gap-2 rounded-lg bg-slate-100 px-2.5 py-1.5">
                <Search className="h-3 w-3 text-slate-600" />
                <span className="text-[11px] text-slate-600">Müşteri, cihaz veya servis no ara…</span>
              </div>
              <Bell className="h-3.5 w-3.5 text-slate-600" />
              <span className="grid h-6 w-6 place-items-center rounded-full bg-vurgu-500/10 text-[10px] font-bold text-vurgu-600">
                AA
              </span>
            </div>

            {sekme === 'servis' && <ServisEkrani />}
            {sekme === 'cari'   && <CariEkrani />}
            {sekme === 'stok'   && <StokEkrani />}
            {sekme === 'rapor'  && <RaporEkrani />}
          </div>
        </div>
      </div>

      <p className="mt-3 text-center text-[11px] text-slate-600">
        Arayüz temsilîdir; ekrandaki veriler örnektir.
      </p>
    </div>
  );
};

export default UygulamaGorunumu;
